import { Colors } from 'constants/ui';

// Matches Backend.Accounts.ServiceProvider's @brand_palette exactly (server-
// validated) and mobile/src/screens/provider/Branding/scene/constants.ts's
// BRAND_PALETTE — this is the real, shipped accent/border palette for the
// `custom_branding` feature, not a landing-page-only invention.
export const BRAND_ACCENT_PALETTE = [
  '#0B1220',
  '#0EA5E9',
  '#F59E0B',
  '#10B981',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#64748B',
] as const;

// Shared alpha levels for tinting a background/border with an arbitrary
// accent hex — used anywhere an accent color needs a soft background rather
// than a solid fill (trust badges, the reputation panel).
export const ACCENT_TINT_BG_ALPHA = 0.12;
export const ACCENT_TINT_BORDER_ALPHA = 0.32;

export const HERO_TRUST_ITEMS = [
  'Free',
  'No website needed',
  'Takes about 10 minutes',
];

export const PRO_CATEGORIES: LandingV2.PersonaCategory[] = [
  { label: 'Hairstylist' },
  { label: 'Nail tech' },
  { label: 'Photographer' },
  { label: 'DJ' },
  { label: 'Plumber' },
  { label: 'Electrician' },
  { label: 'Nanny' },
  { label: 'Cleaner' },
  { label: 'Makeup artist' },
  { label: 'Tutor' },
  { label: 'Physio' },
  { label: 'Caterer' },
];

export const CREATOR_CATEGORIES: LandingV2.PersonaCategory[] = [
  { label: 'Comedian' },
  { label: 'Vlogger' },
  { label: 'Dancer' },
  { label: 'Podcaster' },
  { label: 'Musician' },
  { label: 'Beauty creator' },
  { label: 'Food creator' },
  { label: 'Gamer' },
  { label: 'Actor' },
  { label: 'Fitness creator' },
];

export const PRO_PROFILE: LandingV2.ProfileCardData = {
  name: 'Nomvula Dube',
  subtitle: 'Hairdresser · Braids & weaves · Soweto',
  tagline: 'Neat hands, no rushing, hair that lasts',
  avatarVariant: 'a',
  avatarBg: Colors.avatarPinkBg,
  avatarFg: Colors.avatarPinkFg,
  badges: [
    { variant: 'gold', label: 'Top Rated' },
    { variant: 'purple', label: 'Pro Member' },
    { variant: 'green', label: 'Open for Bookings' },
  ],
  socials: ['facebook', 'instagram', 'tiktok'],
  ctaLabel: 'Book Nomvula',
  stat: {
    countLabel: 'bookings done',
    countValue: '120+',
    score: '4.9',
    reviewCount: 16,
  },
  featuredReview: {
    reviewerName: 'Lerato M.',
    serviceName: 'Box braids',
    rating: 5,
    comment:
      'Booked her for braids before a wedding. Showed up on time, work was neat, and she messaged updates the whole time. Already rebooked for next month.',
    daysAgo: '3 days ago',
  },
  handle: 'kudable.com/p/nomvuladube',
  headerHeadline: 'One link. Everything a client needs to say yes.',
  headerHeadlineHighlight: 'yes',
};

export const CREATOR_PROFILE: LandingV2.ProfileCardData = {
  name: 'Thabo Molefe',
  subtitle: 'Content creator · Comedy · Johannesburg',
  tagline: 'Skits that people finish and share',
  avatarVariant: 'b',
  avatarBg: Colors.avatarSandBg,
  avatarFg: Colors.avatarSandFg,
  badges: [
    { variant: 'gold', label: 'Top Rated' },
    { variant: 'purple', label: 'Pro Member' },
    { variant: 'green', label: 'Open to collabs' },
  ],
  socials: ['facebook', 'instagram', 'tiktok'],
  ctaLabel: 'Work with Thabo',
  stat: {
    countLabel: 'campaigns done',
    countValue: '38',
    score: '4.9',
    reviewCount: 22,
  },
  featuredReview: {
    reviewerName: 'Sipho K.',
    serviceName: 'Product launch campaign',
    rating: 5,
    comment:
      'Turnaround was faster than any agency we tried. Briefed him on a Monday, three posts were live by Thursday, and engagement beat our usual numbers.',
    daysAgo: '1 week ago',
  },
  handle: 'kudable.com/p/thabomolefe',
  headerHeadline: 'One link. Everything a brand needs to say yes.',
  headerHeadlineHighlight: 'yes',
  metrics: [
    { value: '1.2M', label: 'followers' },
    { value: '6.8%', label: 'engagement' },
    { value: '240k', label: 'avg views' },
  ],
};

export const PRO_HERO_HIGHLIGHTS: LandingV2.FeatureItem[] = [
  {
    title: 'One link in your bio',
    description: 'No more explaining prices in DMs.',
  },
  {
    title: 'Reviews on your side',
    description: 'From paid jobs, so they carry weight.',
  },
  {
    title: 'Found on the marketplace',
    description: 'Where clients come looking for you.',
  },
];

export const HOW_IT_WORKS_STEPS: LandingV2.StepItem[] = [
  {
    number: 1,
    title: 'Create your page',
    description:
      'Your services, your prices, your photos. Free, about ten minutes.',
  },
  {
    number: 2,
    title: 'Share the link',
    description:
      'In your bio, in your captions, or to anyone who asks what you charge.',
  },
  {
    number: 3,
    title: 'Get booked',
    description:
      'Clients pick a date from your calendar and pay a deposit to hold it.',
  },
  {
    number: 4,
    title: 'Collect reviews',
    description:
      'Every finished job can be reviewed. Your reputation builds itself.',
    accent: 'highlight',
  },
];

export const PRO_FEATURES: LandingV2.FeatureItem[] = [
  {
    title: 'Reviews from real bookings',
    description:
      'Only clients who paid you can leave one, so nobody doubts them.',
  },
  {
    title: 'Your prices, in the open',
    description: 'No back and forth before anyone knows the number.',
  },
  {
    title: 'Your work, in one place',
    description: 'Photos of past jobs, not a feed they have to scroll.',
  },
  {
    title: 'A calendar that takes deposits',
    description: 'They choose a date, pay to hold it, and show up.',
  },
];

export const PRO_MARKETPLACE_LISTINGS: LandingV2.MarketplaceListing[] = [
  {
    avatarVariant: 'a',
    avatarBg: Colors.avatarPinkBg,
    avatarFg: Colors.avatarPinkFg,
    title: 'Hairdressers near Soweto',
    subtitle: '42 pros · from R180',
  },
  {
    avatarVariant: 'b',
    avatarBg: Colors.avatarSandBg,
    avatarFg: Colors.avatarSandFg,
    title: 'Plumbers, same-day',
    subtitle: '18 pros · callout from R450',
  },
  {
    avatarVariant: 'a',
    avatarBg: Colors.avatarPinkBg,
    avatarFg: Colors.avatarPinkFg,
    title: 'Nannies, live-in',
    subtitle: '27 pros · vetted references',
  },
];

export const CREATOR_MARKETPLACE_LISTINGS: LandingV2.MarketplaceListing[] = [
  {
    avatarVariant: 'b',
    avatarBg: Colors.avatarSandBg,
    avatarFg: Colors.avatarSandFg,
    title: 'Comedy creators, 500k+',
    subtitle: '31 creators · from R18k',
  },
  {
    avatarVariant: 'a',
    avatarBg: Colors.avatarPinkBg,
    avatarFg: Colors.avatarPinkFg,
    title: 'Beauty creators, Gauteng',
    subtitle: '24 creators · from R6k',
  },
  {
    avatarVariant: 'b',
    avatarBg: Colors.avatarSandBg,
    avatarFg: Colors.avatarSandFg,
    title: 'Podcasters for brand reads',
    subtitle: '12 creators · from R4k',
  },
];

export const BRAND_COLLABS: LandingV2.BrandCollab[] = [
  {
    icon: 'energy',
    name: 'Torque Energy',
    date: 'Oct 2025',
    avatarBg: 'ink',
    description: '3 skits and a launch appearance',
    tags: ['Paid', '2.4M views'],
  },
  {
    icon: 'sneaker',
    name: 'Zephyr Sneakers',
    date: 'Mar 2026',
    avatarBg: 'accent',
    description: '6-month ambassador, monthly content',
    tags: ['Paid', 'Ambassador'],
  },
  {
    icon: 'cosmetics',
    name: 'Glorae Cosmetics',
    date: 'Jun 2026',
    avatarBg: 'pink',
    description: 'Product launch, 2 posts and a story set',
    tags: ['Paid', 'Promo code'],
  },
];

export const BRAND_TESTIMONIAL = {
  quote:
    'He wrote the skit, shot it and posted on time. Our page grew more in that week than the whole quarter.',
  attribution: 'Lindiwe N. · brand manager, Sandton · verified campaign',
};

export const CREATOR_CRITERIA: LandingV2.CriteriaItem[] = [
  {
    number: 1,
    title: 'Relevance to the brief',
    description:
      "The first filter, every time. Your categories, your style and the work you've done say whether you fit before anyone counts followers.",
  },
  {
    number: 2,
    title: 'The numbers behind reach',
    description:
      'Followers, engagement rate and average views on one line, so nobody has to ask for a screenshot.',
  },
  {
    number: 3,
    title: "Who you've worked with",
    description:
      'Not just the logo — what you actually made for them, and how it landed.',
  },
  {
    number: 4,
    title: 'Easy to work with',
    description:
      'Delivered on time, on brief, spelled right. Reviews from paid campaigns are where that shows up.',
  },
  {
    number: 5,
    title: 'Original work in your own style',
    description:
      'Your portfolio sits next to your rates, so they see the work before they see the price.',
  },
  {
    number: 6,
    title: 'Going past the brief',
    description:
      "Three videos when they asked for one. Reviewers can say that on your page, and it's what gets you rebooked.",
    accent: 'highlight',
  },
];

export const BOOKING_TYPE_CARDS: LandingV2.BookingTypeCard[] = [
  {
    title: 'Paid bookings',
    description:
      'Appearances, shows, shoots, sessions. Someone who just watched your video can book a date without a single DM.',
    bullets: [
      'Your rates and free dates, live',
      'Deposit paid when they book',
      'Nothing bookable? It becomes a "Work with me" page',
    ],
    variant: 'light',
  },
  {
    title: 'Collabs & sponsorships',
    description:
      "Brands want proof before they pay, and they want it in one place. Not a folder of screenshots you assemble every time.",
    bullets: [
      "Brands you've worked with, listed",
      'Reviews from paid campaigns',
      "A rate card that's always current",
    ],
    variant: 'dark',
  },
];
